import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { Description as PdfIcon } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || '';
const TEAL = '#0d9488';
const TEAL_DARK = '#0f766e';

const thSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#64748b',
  bgcolor: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
  py: 1.25
};

export default function StaffingHrPolicyTab({ apiRequest, token, onFeedback }) {
  const [doc, setDoc] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiRequest('/api/company/hrms/hr-policy/document');
      setDoc(data.document || null);
    } catch (e) {
      onFeedback(e.message);
      setDoc(null);
    } finally {
      setLoading(false);
    }
  }, [apiRequest, onFeedback]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async () => {
    if (!pdfFile) {
      onFeedback('Choose a PDF file first.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', pdfFile);
      const res = await fetch(`${API_URL}/api/company/hrms/hr-policy/document`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      onFeedback(data.message || 'HR policy PDF uploaded. Employees can view it in the app.');
      setPdfFile(null);
      await load();
    } catch (e) {
      onFeedback(e.message);
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove the HR policy PDF? Employees will no longer see it in the app.')) return;
    try {
      await apiRequest('/api/company/hrms/hr-policy/document', { method: 'DELETE' });
      onFeedback('HR policy PDF removed.');
      await load();
    } catch (e) {
      onFeedback(e.message);
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1}>
        <Box>
          <Typography variant="h6" fontWeight={800} color="#0f172a">
            HR policy
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload your company HR handbook / policy PDF. Active employees can open it inside the mobile app.
          </Typography>
        </Box>
        <Chip
          icon={<PdfIcon />}
          label={doc ? 'Published to app' : 'Not uploaded'}
          color={doc ? 'success' : 'warning'}
          variant={doc ? 'filled' : 'outlined'}
        />
      </Stack>

      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Separate from the <strong>Leave policy</strong> PDF under Leave Policies. HR policy appears under Documents in the
        candidate app and in the staffing hub on web.
      </Alert>

      <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}>
        <CardContent>
          {loading ? (
            <Typography color="text.secondary">Loading…</Typography>
          ) : doc ? (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={thSx}>File</TableCell>
                    <TableCell sx={thSx}>Uploaded</TableCell>
                    <TableCell sx={thSx} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography fontWeight={600}>{doc.original_filename}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {doc.view_url && (
                        <Button size="small" variant="outlined" href={doc.view_url} target="_blank" rel="noreferrer" sx={{ mr: 0.5 }}>
                          Preview
                        </Button>
                      )}
                      <Button size="small" color="error" variant="outlined" onClick={remove}>
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
              No HR policy PDF uploaded yet.
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" component="label">
              Choose PDF
              <input type="file" hidden accept="application/pdf,.pdf" onChange={(e) => setPdfFile(e.target.files?.[0] || null)} />
            </Button>
            <Typography variant="body2" color="text.secondary">{pdfFile?.name || 'No file selected'}</Typography>
            <Button variant="contained" disabled={!pdfFile || uploading} onClick={upload} sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK } }}>
              {uploading ? 'Uploading…' : doc ? 'Replace PDF' : 'Upload HR policy'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
